// PrimeTimeView.js - "Prime time" del evento: ranking de inscritos por DOTS (hombres y mujeres)
// para elegir quiénes compiten en el bloque estelar. El umbral no filtra: solo destaca filas.
import React, { useEffect, useMemo, useState } from 'react';
import { getEventoById, getInscritos } from '../../../services/eventosService';

const styles = {
  container: { padding: '24px', maxWidth: '1200px' },
  title: { margin: 0, fontSize: '24px', fontWeight: 700 },
  subtitle: { margin: '6px 0 16px', color: '#666', fontSize: '14px', maxWidth: 760, lineHeight: 1.5 },
  btnGhost: {
    padding: '10px 16px', background: '#f4f6f8', color: '#111', border: '1px solid #ddd',
    borderRadius: '8px', cursor: 'pointer', fontWeight: 600, marginBottom: 16,
  },
  btnSecondary: {
    padding: '9px 14px', background: '#fff', color: '#1a4d8c', border: '1px solid #1a4d8c',
    borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 520px), 1fr))', gap: 20, alignItems: 'start' },
  card: { background: '#fff', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', padding: '18px' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  cardTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  controls: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '12px',
    background: '#fafafa', border: '1px solid #eee', borderRadius: 10, marginBottom: 12, fontSize: 13,
  },
  select: { padding: '7px 8px', border: '1px solid #ccc', borderRadius: 8, fontSize: 13, background: '#fff' },
  input: { width: 90, padding: '7px 8px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14 },
  tableWrap: { overflowX: 'auto', maxHeight: '65vh', overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '9px 8px', borderBottom: '2px solid #e0e0e0', color: '#555', textAlign: 'left',
    whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff', zIndex: 1,
  },
  td: { padding: '8px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' },
};

const COLOR_PRIME = '#fff3c4';
const BORDE_PRIME = '#f0b400';

const txt = (v) => String(v ?? '').trim().toLowerCase();
const generoDe = (i) => {
  const g = txt(i.genero);
  if (['f', 'femenino', 'female', 'mujer'].includes(g)) return 'F';
  if (['m', 'masculino', 'male', 'hombre'].includes(g)) return 'M';
  return null;
};
const testeoDe = (i) => {
  const d = txt(i.division);
  if (d.includes('untested')) return 'Untested';
  if (d.includes('tested')) return 'Tested';
  if (i.categoria_peso_untested && !i.categoria_peso_tested) return 'Untested';
  if (i.categoria_peso_tested) return 'Tested';
  return '';
};
const pesoDe = (i) => (testeoDe(i) === 'Untested' ? i.categoria_peso_untested : i.categoria_peso_tested) || i.categoria_peso || '';
const nombreDe = (i) => `${i.nombre_pila || ''} ${i.apellido || ''}`.trim() || i.nombre || i.rut || '';
const dotsDe = (i) => (i.dots != null && !Number.isNaN(Number(i.dots)) ? Number(i.dots) : 0);

/** Ranking por DOTS de mayor a menor (sin datos = 0, al final), desempate por nombre. */
const rankear = (lista) => [...lista]
  .sort((a, b) => dotsDe(b) - dotsDe(a) || nombreDe(a).localeCompare(nombreDe(b), 'es'))
  .map((i, idx) => ({ ...i, puesto: idx + 1 }));

/** ¿Entra al prime time? Por cantidad (top N) o por DOTS mínimo. Umbral vacío = nadie. */
const esPrime = (fila, umbral) => {
  const valor = Number(umbral.valor);
  if (umbral.valor === '' || Number.isNaN(valor) || valor <= 0) return false;
  return umbral.modo === 'dots' ? dotsDe(fila) >= valor : fila.puesto <= valor;
};

const leerUmbrales = (clave) => {
  try {
    const data = JSON.parse(window.localStorage.getItem(clave) || 'null');
    if (data && data.M && data.F) return data;
  } catch { /* sin almacenamiento */ }
  return { M: { modo: 'top', valor: '' }, F: { modo: 'top', valor: '' } };
};

const csvEscape = (v) => {
  const s = String(v ?? '');
  return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const descargarCsv = (filas, umbral, sexo, eventoId, nombreEvento) => {
  const encabezados = [
    'puesto', 'prime_time', 'nombre', 'rut', 'club', 'division', 'categoria_peso',
    'division_edad', 'modalidad', 'dots', 'anio_dots', 'estado',
  ];
  const lineas = [encabezados.join(',')];
  filas.forEach((f) => {
    lineas.push([
      f.puesto, esPrime(f, umbral) ? 'SI' : 'NO', nombreDe(f), f.rut || '', f.club || '',
      testeoDe(f), pesoDe(f), f.division_edad || f.categoria_edad || '', f.modalidad || '',
      dotsDe(f).toFixed(2), f.dots_anio || '', f.estado || '',
    ].map(csvEscape).join(','));
  });
  const criterio = umbral.valor
    ? (umbral.modo === 'dots' ? `DOTS >= ${umbral.valor}` : `Top ${umbral.valor}`)
    : 'sin umbral';
  lineas.push('');
  lineas.push(csvEscape(`# ${nombreEvento || `Evento ${eventoId}`} - Prime time ${sexo === 'M' ? 'hombres' : 'mujeres'}: ${criterio}`));
  // BOM: para que Excel muestre bien tildes y ñ
  const blob = new Blob(['﻿' + lineas.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `prime_time_${sexo === 'M' ? 'hombres' : 'mujeres'}_evento_${eventoId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const TablaRanking = ({ titulo, sexo, filas, umbral, onUmbral, eventoId, nombreEvento }) => {
  const elegidos = filas.filter((f) => esPrime(f, umbral)).length;
  const sinDots = filas.filter((f) => dotsDe(f) === 0).length;
  return (
    <section style={styles.card}>
      <div style={styles.cardHead}>
        <h2 style={styles.cardTitle}>{titulo} <span style={{ color: '#888', fontWeight: 500, fontSize: 14 }}>({filas.length})</span></h2>
        <button
          type="button"
          style={styles.btnSecondary}
          onClick={() => descargarCsv(filas, umbral, sexo, eventoId, nombreEvento)}
          disabled={filas.length === 0}
        >
          Descargar ranking
        </button>
      </div>

      <div style={styles.controls}>
        <strong>Prime time:</strong>
        <select
          style={styles.select}
          value={umbral.modo}
          onChange={(e) => onUmbral({ ...umbral, modo: e.target.value })}
          aria-label={`Tipo de umbral ${titulo}`}
        >
          <option value="top">Los primeros (cantidad)</option>
          <option value="dots">DOTS mínimo</option>
        </select>
        <input
          type="number"
          min="0"
          step={umbral.modo === 'dots' ? '0.01' : '1'}
          style={styles.input}
          value={umbral.valor}
          placeholder={umbral.modo === 'dots' ? 'ej. 400' : 'ej. 10'}
          onChange={(e) => onUmbral({ ...umbral, valor: e.target.value })}
          aria-label={`Umbral ${titulo}`}
        />
        <span style={{ color: elegidos ? '#8a6100' : '#888' }}>
          {elegidos ? <>✓ <strong>{elegidos}</strong> en prime time</> : 'Define un umbral para destacar'}
        </span>
      </div>

      {filas.length === 0 ? (
        <p style={{ color: '#888', margin: '8px 0' }}>No hay inscritos.</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Nombre</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>DOTS</th>
                <th style={styles.th}>División</th>
                <th style={styles.th}>Peso</th>
                <th style={styles.th}>Club</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => {
                const prime = esPrime(f, umbral);
                const fondo = prime ? { background: COLOR_PRIME } : null;
                return (
                  <tr key={f.id}>
                    <td style={{ ...styles.td, ...fondo, fontWeight: 700, borderLeft: `4px solid ${prime ? BORDE_PRIME : 'transparent'}` }}>{f.puesto}</td>
                    <td style={{ ...styles.td, ...fondo, fontWeight: prime ? 700 : 400, whiteSpace: 'normal', minWidth: 150 }}>{nombreDe(f)}</td>
                    <td style={{ ...styles.td, ...fondo, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {dotsDe(f) > 0 ? (
                        <>
                          <strong>{dotsDe(f).toFixed(2)}</strong>
                          {f.dots_anio && <span style={{ color: '#999', fontSize: 11, marginLeft: 4 }}>({f.dots_anio})</span>}
                        </>
                      ) : (
                        <span style={{ color: '#aaa' }} title="Sin resultados en los últimos 2 rankings">0</span>
                      )}
                    </td>
                    <td style={{ ...styles.td, ...fondo, color: '#555' }}>{testeoDe(f) || '—'}</td>
                    <td style={{ ...styles.td, ...fondo, color: '#555' }}>{pesoDe(f) || '—'}</td>
                    <td style={{ ...styles.td, ...fondo, color: '#555', whiteSpace: 'normal', minWidth: 110 }}>{f.club || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {sinDots > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#888' }}>
          {sinDots} sin resultados en los últimos 2 rankings (DOTS 0).
        </p>
      )}
    </section>
  );
};

const PrimeTimeView = ({ eventoId, onBack }) => {
  const [evento, setEvento] = useState(null);
  const [inscritos, setInscritos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [soloConfirmados, setSoloConfirmados] = useState(true);
  const claveUmbrales = `achipo_prime_time_${eventoId}`;
  const [umbrales, setUmbrales] = useState(() => leerUmbrales(claveUmbrales));

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    Promise.all([getEventoById(eventoId).catch(() => null), getInscritos(Number(eventoId))])
      .then(([ev, lista]) => {
        if (cancelado) return;
        setEvento(ev);
        setInscritos(Array.isArray(lista) ? lista : []);
      })
      .catch((e) => { if (!cancelado) setError(e.message || 'No se pudieron cargar los inscritos'); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [eventoId]);

  const cambiarUmbral = (sexo, valor) => {
    setUmbrales((prev) => {
      const nuevo = { ...prev, [sexo]: valor };
      try { window.localStorage.setItem(claveUmbrales, JSON.stringify(nuevo)); } catch { /* sin almacenamiento */ }
      return nuevo;
    });
  };

  const { hombres, mujeres, sinGenero } = useMemo(() => {
    const activos = inscritos.filter((i) => {
      const estado = txt(i.estado);
      if (estado === 'cancelado') return false;
      return soloConfirmados ? estado === 'confirmado' : true;
    });
    return {
      hombres: rankear(activos.filter((i) => generoDe(i) === 'M')),
      mujeres: rankear(activos.filter((i) => generoDe(i) === 'F')),
      sinGenero: activos.filter((i) => !generoDe(i)).length,
    };
  }, [inscritos, soloConfirmados]);

  const nombreEvento = evento?.nombre || evento?.data?.nombre || '';

  return (
    <div style={styles.container}>
      <button type="button" style={styles.btnGhost} onClick={onBack}>← Volver</button>
      <h1 style={styles.title}>Prime time{nombreEvento ? ` · ${nombreEvento}` : ''}</h1>
      <p style={styles.subtitle}>
        Ranking de los inscritos por DOTS, sin importar división ni categoría. El DOTS es el mejor
        del atleta en los últimos 2 rankings anuales; quien no ha competido aparece con 0. Define un
        umbral en cada tabla para destacar a quienes compiten en el prime time.
      </p>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={soloConfirmados} onChange={(e) => setSoloConfirmados(e.target.checked)} />
        Solo inscritos confirmados
      </label>

      {cargando ? (
        <p style={{ color: '#666' }}>Cargando inscritos…</p>
      ) : error ? (
        <p style={{ color: '#c62828', fontWeight: 600 }}>{error}</p>
      ) : (
        <>
          {sinGenero > 0 && (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#b26a00' }}>
              {sinGenero} inscrito{sinGenero !== 1 ? 's' : ''} sin género registrado no aparece{sinGenero !== 1 ? 'n' : ''} en las tablas.
            </p>
          )}
          <div style={styles.grid}>
            <TablaRanking
              titulo="Hombres" sexo="M" filas={hombres} umbral={umbrales.M}
              onUmbral={(u) => cambiarUmbral('M', u)} eventoId={eventoId} nombreEvento={nombreEvento}
            />
            <TablaRanking
              titulo="Mujeres" sexo="F" filas={mujeres} umbral={umbrales.F}
              onUmbral={(u) => cambiarUmbral('F', u)} eventoId={eventoId} nombreEvento={nombreEvento}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PrimeTimeView;
